// Bugsnag noise rules, applied in Bugsnag.start's onError (utils/bugsnag.ts). Kept separate so
// they can be unit tested without starting a real Bugsnag session.

type Frame = { file?: string | null };
type ReportedError = { errorClass?: string; errorMessage?: string; stacktrace?: Frame[] };
export type FilterableEvent = { errors: ReportedError[]; severity?: string };

// What each browser engine says when a fetch() fails at the network level: connection dropped,
// request cancelled because the tab/app was closed or backgrounded (very common on iOS), or
// blocked. Raised by library code (MSAL, Teams SDK, next-pwa) with no stack into the app.
const NETWORK_FAILURE_MESSAGES = new Set([
  'Load failed', // Safari / every iOS browser (all WebKit)
  'Failed to fetch', // Chrome, Edge, Android WebView
  'NetworkError when attempting to fetch resource.', // Firefox
]);

// React's minified hydration errors (418 mismatch, 423 fallback to client rendering, 425 text
// mismatch). Most web logins used to hit these (AppHeader matched "qr" against the random #code=
// in asPath - fixed 2026-10-06); what's left should come from the user's environment changing the
// page first - translation, extensions, in-app browsers injecting scripts. Harmless (React
// re-renders), but kept as `info` rather than dropped: a sudden jump would still show if a code
// change ever caused a real mismatch.
const HYDRATION_ERROR = /Minified React error #(418|423|425)\b/;

// The browser's own report that ResizeObserver callbacks changed the layout again within the same
// frame, so the rest of the notifications were moved to the next one (Chrome now; the second is the
// older wording). Fired as a window error with no stack - typically right after login, while the
// loader is swapped for the page. Nothing is lost and there's nothing to debug.
const RESIZE_OBSERVER_LOOP = /^ResizeObserver loop (completed with undelivered notifications|limit exceeded)/;

// Teams couldn't get a token for reasons on the user's side:
// - CancelledByUser: Teams had to show its consent/sign-in popup (first use, or adding the app to a
//   meeting from /teamExtension) and the user closed it.
// - NoNetwork: the token request never reached Microsoft - e.g. a school/company proxy that wants its
//   own credentials ("Invalid authentication credentials for a proxy server"), seen 2026-10-07.
// Kept as `info` rather than dropped: many from one school point at missing admin consent or its proxy.
const isTeamsAuthUserSide = (error: ReportedError) =>
  error.errorClass === 'TeamsAuthTokenError' && /\b(CancelledByUser|NoNetwork)\b/.test(error.errorMessage ?? '');

// Browser extensions' own errors: their content scripts run inside the page, so Bugsnag sees them too
// (password managers, translators, ad blockers...). E.g. "Invalid call to runtime.sendMessage(). Tab not
// found." (2026-10-07) - an extension messaging a tab that had closed. Never from Attender's code.
const EXTENSION_MESSAGE = /\b(runtime\.sendMessage|runtime\.connect)\b|Extension context invalidated|Receiving end does not exist/i;
const EXTENSION_FILE = /^(chrome|moz|safari(-web)?)-extension:\/\//i;

const isFromExtension = (error: ReportedError) =>
  (error.stacktrace ?? []).some((frame) => EXTENSION_FILE.test(frame.file ?? '')) ||
  (EXTENSION_MESSAGE.test(error.errorMessage ?? '') && !hasStackIntoCode(error));

const hasStackIntoCode = (error: ReportedError) =>
  (error.stacktrace ?? []).some((frame) => !!frame.file && frame.file !== 'unknown' && !EXTENSION_FILE.test(frame.file));

/** Returns false to drop the event; may lower its severity. */
export function bugsnagNoiseFilter(event: FilterableEvent): boolean {
  const error = event.errors?.[0];
  if (!error) return true;

  if (
    error.errorClass === 'TypeError' &&
    NETWORK_FAILURE_MESSAGES.has((error.errorMessage ?? '').trim()) &&
    !hasStackIntoCode(error)
  ) {
    return false;
  }

  if (RESIZE_OBSERVER_LOOP.test((error.errorMessage ?? '').trim()) && !hasStackIntoCode(error)) {
    return false;
  }

  if (isFromExtension(error)) {
    return false;
  }

  if (HYDRATION_ERROR.test(error.errorMessage ?? '') || isTeamsAuthUserSide(error)) {
    event.severity = 'info';
  }

  // A part of the app fetched on demand didn't download - the device's connection, not the code (the
  // file was there). The inline chunkReloadScript reloads the page once for it. `warning`, not dropped:
  // a jump right after a release would mean a release really is missing files.
  if (error.errorClass === 'ChunkLoadError') {
    event.severity = 'warning';
  }

  return true;
}

// The MSAL redirect lands on /#code=...&client_info=...&session_state=...&state=..., and Bugsnag's
// navigation breadcrumbs record those URLs in full (message, from/to, history state.as). The code is
// single-use and already redeemed by then, but login codes, session ids and client_info (user and
// tenant ids) have no business in a third-party service - so the values are blanked out.
const AUTH_PARAM = /([#?&])(code|client_info|session_state|state|id_token|access_token|refresh_token)=[^&#\s"']*/g;

export const redactAuthParams = (text: string): string => text.replace(AUTH_PARAM, '$1$2=[redacted]');

function redactDeep(value: unknown, depth = 0): unknown {
  if (typeof value === 'string') return redactAuthParams(value);
  if (!value || typeof value !== 'object' || depth > 5) return value;
  if (Array.isArray(value)) return value.map((v) => redactDeep(v, depth + 1));
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(value)) out[k] = redactDeep(v, depth + 1);
  return out;
}

export type FilterableBreadcrumb = { message?: string; metadata?: Record<string, unknown> };

/** Applied in Bugsnag.start's onBreadcrumb - strips auth params from every breadcrumb. */
export function bugsnagBreadcrumbFilter(breadcrumb: FilterableBreadcrumb): boolean {
  if (breadcrumb.message) breadcrumb.message = redactAuthParams(breadcrumb.message);
  if (breadcrumb.metadata) breadcrumb.metadata = redactDeep(breadcrumb.metadata) as Record<string, unknown>;
  return true;
}
